import { appendSearchParams } from '@alexis/helpers/searchParam';
import sha256 from 'crypto-js/sha256';
import type { CabinClass } from 'flights/cabinClass';
import type { FlightSearchResult } from 'flights/metasearch/flightSearchResult';
import { BehaviorSubject, merge, of, share, type Observable } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { concatMap, delay, filter, switchMap } from 'rxjs/operators';

import { localStorageGet, localStorageSave } from '@wego/alexis/helpers/localStorage';

import { FLIGHT_SEARCH_RESPONSE_LOCAL_STORAGE_KEY } from '@constants/localStorage';

import { checkHasSabreFare, getOfferEngineIntegrationAbTestVariant } from '@helpers/flight';
import { getGenzoIdentifierId } from '@helpers/genzoIdentifierId';

import type { User } from '@wegoTypes/user';

type CachedFlightSearchResponse = {
  cacheKey: string;
  flightSearchResult: FlightSearchResult;
  expiryTime: number;
};

type StopRecordingFetchTimeUntilFirstSabreFareResult = (data: {
  flight_offer_engine_integration_ab_test_variant: string | undefined;
}) => void;

type RecordFirstPollWithSabreFareAction = (data: { index: number }) => void;

export function flightSearchObservable(
  url: string,
  currentSiteCode: string,
  locale: string,
  currencyCode: string,
  legs: Array<Leg>,
  adultCount: number,
  childrenCount: number,
  infantCount: number,
  cabinClass: CabinClass,
  paymentMethodIds: Array<number>,
  user: User | null,
  liveTestProviderCodes: Array<string>,
  shopcashClickId: string,
  wegoAnalyticsClientId: string | undefined,
  wegoAnalyticsSessionId: string | undefined,
  updateProgressPercent: () => void,
  recordNumberOfFaresInFirstPoll: (numberOfFares: number) => void,
  startRecordingTimeForEachPoll: () => void,
  stopRecordingTimeForEachPoll: (numberOfFares: number) => void,
  startRecordingSearchApiLatency: () => void,
  stopRecordingSearchApiLatency: () => void,
  startRecordingSearchResponseProcessingTime: () => void,
  stopRecordingSearchResponseProcessingTime: () => void,
  startRecordingPollingLatency: () => void,
  stopRecordingPollingLatency: (data: { index: number }) => void,
  startRecordingPollingProcessingTime: () => void,
  stopRecordingPollingProcessingTime: (data: { index: number; numberOfFares: number }) => void,
  stopRecordingFetchTimeUntilFirstSabreFareResult: StopRecordingFetchTimeUntilFirstSabreFareResult,
  recordFirstPollWithSabreFareAction: RecordFirstPollWithSabreFareAction,
  startRecordingPollingProcessingTimeAndWaitingTime: () => void,
  stopRecordingPollingProcessingTimeAndWaitingTime: (data: { index: number }) => void,
  showWegoFaresOnly: boolean,
  isOfferEngineEnabled: boolean,
  partnerDebugKey: string | null,
  providerRestrictions: string[],
  setPollIndex: (index: number) => void,
  isCacheFlightSearch: boolean,
  showTurnstileWidget: () => void,
  optionalQueryStrings?: Record<string, any>,
): Observable<FlightSearchResult> {
  const flightSearchIdObservable$ = flightSearchIdObservable(
    url,
    currentSiteCode,
    locale,
    currencyCode,
    legs,
    adultCount,
    childrenCount,
    infantCount,
    cabinClass,
    paymentMethodIds,
    user,
    'WEB_APP',
    'DESKTOP',
    liveTestProviderCodes,
    shopcashClickId,
    wegoAnalyticsClientId,
    wegoAnalyticsSessionId,
    showWegoFaresOnly,
    isOfferEngineEnabled,
    partnerDebugKey,
    providerRestrictions,
    isCacheFlightSearch,
    showTurnstileWidget,
    startRecordingSearchApiLatency,
    stopRecordingSearchApiLatency,
    startRecordingSearchResponseProcessingTime,
    optionalQueryStrings,
  );

  return flightSearchResultObservable(
    flightSearchIdObservable$,
    url,
    locale,
    currencyCode,
    wegoAnalyticsClientId,
    updateProgressPercent,
    recordNumberOfFaresInFirstPoll,
    startRecordingTimeForEachPoll,
    stopRecordingTimeForEachPoll,
    stopRecordingSearchResponseProcessingTime,
    startRecordingPollingLatency,
    stopRecordingPollingLatency,
    startRecordingPollingProcessingTime,
    stopRecordingPollingProcessingTime,
    stopRecordingFetchTimeUntilFirstSabreFareResult,
    recordFirstPollWithSabreFareAction,
    startRecordingPollingProcessingTimeAndWaitingTime,
    stopRecordingPollingProcessingTimeAndWaitingTime,
    setPollIndex,
    isCacheFlightSearch,
  );
}

export function flightSearchResultObservable(
  flightSearchIdObservable: Observable<FlightSearchResult>,
  url: string,
  locale: string,
  currencyCode: string,
  wegoAnalyticsClientId: string | undefined,
  updateProgressPercent: () => void,
  recordNumberOfFaresInFirstPoll: (numberOfFares: number) => void,
  startRecordingTimeForEachPoll: () => void,
  stopRecordingTimeForEachPoll: (numberOfFares: number) => void,
  stopRecordingSearchResponseProcessingTime: () => void,
  startRecordingPollingLatency: () => void,
  stopRecordingPollingLatency: (data: { index: number }) => void,
  startRecordingPollingProcessingTime: () => void,
  stopRecordingPollingProcessingTime: (data: { index: number; numberOfFares: number }) => void,
  stopRecordingFetchTimeUntilFirstSabreFareResult: StopRecordingFetchTimeUntilFirstSabreFareResult,
  recordFirstPollWithSabreFareAction: RecordFirstPollWithSabreFareAction,
  startRecordingPollingProcessingTimeAndWaitingTime: () => void,
  stopRecordingPollingProcessingTimeAndWaitingTime: (data: { index: number }) => void,
  setPollIndex: (index: number) => void,
  isCacheFlightSearch: boolean,
): Observable<FlightSearchResult> {
  const flightSearchResultObservable = flightSearchIdObservable.pipe(
    concatMap((flightSearchIdResultResponse: FlightSearchResult) => {
      let hasFirstSabreFare = false;

      const subject = new BehaviorSubject<{ offset: number; isLastPolling: boolean }>({
        offset: 0,
        isLastPolling: false,
      });

      return subject.pipe(
        concatMap((value: { offset: number; isLastPolling: boolean }, index: number) => {
          const searchId = flightSearchIdResultResponse.search.id;

          let isAddDelay = true;

          if (isCacheFlightSearch) {
            const cachedFlightSearchResponses =
              (localStorageGet(
                FLIGHT_SEARCH_RESPONSE_LOCAL_STORAGE_KEY,
              ) as CachedFlightSearchResponse[]) || [];

            const cachedData = cachedFlightSearchResponses.find((cachedFlightSearchResponse) => {
              return (
                cachedFlightSearchResponse.flightSearchResult.search.id ===
                  flightSearchIdResultResponse.search.id &&
                cachedFlightSearchResponse.expiryTime > Date.now()
              );
            });

            if (cachedData) {
              const searchTriggeredTime = new Date(cachedData.flightSearchResult.search.createdAt);
              const timeElapsedFromSearch = Date.now() - searchTriggeredTime.getTime();

              // Backend would have collected the data within 16.5s.
              isAddDelay = timeElapsedFromSearch < 16500;
            }
          }
          if (index === 0) {
            stopRecordingSearchResponseProcessingTime();
          }
          if (index > 0) {
            // This is to record the time taken to process the previous poll
            stopRecordingPollingProcessingTimeAndWaitingTime({ index: index - 1 });
          }
          startRecordingTimeForEachPoll();
          startRecordingPollingLatency();

          return fromFetch(
            appendSearchParams(`${url}/${searchId}/results`, {
              locale,
              currencyCode,
              clientId: wegoAnalyticsClientId,
              offset: value.offset,
              isLastPolling: value.isLastPolling,
            }),
            {
              method: 'GET',
              credentials: 'include',
              mode: 'cors',
              headers: {
                'Content-Type': 'application/json',
                ...(!!getGenzoIdentifierId() && {
                  'X-Wego-Genzo-Identifier-Id': getGenzoIdentifierId()!,
                }),
              },
            },
          ).pipe(
            concatMap<Response, Promise<FlightSearchResult>>(async (response: Response) => {
              updateProgressPercent();
              stopRecordingPollingLatency({ index });
              startRecordingPollingProcessingTime();
              startRecordingPollingProcessingTimeAndWaitingTime();
              if (response.ok) {
                const flightSearchResult = (await response.json()) as FlightSearchResult;

                stopRecordingTimeForEachPoll(flightSearchResult.fares.length);

                setPollIndex(index);
                stopRecordingPollingProcessingTime({
                  index,
                  numberOfFares: flightSearchResult.fares.length,
                });

                if (checkHasSabreFare(flightSearchResult.fares) && !hasFirstSabreFare) {
                  stopRecordingFetchTimeUntilFirstSabreFareResult({
                    flight_offer_engine_integration_ab_test_variant:
                      getOfferEngineIntegrationAbTestVariant(flightSearchResult.search),
                  });
                  recordFirstPollWithSabreFareAction({ index });
                  hasFirstSabreFare = true;
                }

                if (index === 0) {
                  recordNumberOfFaresInFirstPoll(flightSearchResult.fares.length);
                }

                if (index < 10) {
                  of('')
                    .pipe(delay(isAddDelay ? (index + 1) * 300 : 0))
                    .subscribe(() =>
                      subject.next({
                        offset: flightSearchResult.count,
                        isLastPolling: index === 9,
                      }),
                    );
                } else {
                  stopRecordingPollingProcessingTimeAndWaitingTime({ index: 10 });
                  subject.complete();
                }
                return flightSearchResult;
              }

              stopRecordingTimeForEachPoll(0);
              stopRecordingPollingProcessingTimeAndWaitingTime({ index });
              if (index === 0) {
                recordNumberOfFaresInFirstPoll(0);
              }
              throw new Error(response.status.toString());
            }),
            filter(
              ({ fares, trips, scores }: FlightSearchResult) =>
                fares.length > 0 || trips.length > 0 || Object.entries(scores).length > 0,
            ),
            share({
              resetOnError: false,
              resetOnComplete: false,
              resetOnRefCountZero: false,
            }),
          );
        }),
      );
    }),
  );

  return merge(flightSearchIdObservable, flightSearchResultObservable);
}

function flightSearchIdObservable(
  url: string,
  currentSiteCode: string,
  locale: string,
  currencyCode: string,
  legs: Array<Leg>,
  adultCount: number,
  childrenCount: number,
  infantCount: number,
  cabinClass: CabinClass,
  paymentMethodIds: Array<number>,
  user: User | null,
  appType: AppType,
  deviceType: DeviceType,
  liveTestProviderCodes: Array<string>,
  shopcashClickId: string,
  wegoAnalyticsClientId: string | undefined,
  wegoAnalyticsSessionId: string | undefined,
  showWegoFaresOnly: boolean,
  isOfferEngineEnabled: boolean,
  partnerDebugKey: string | null,
  providerRestrictions: string[],
  isCacheFlightSearch: boolean,
  showTurnstileWidget: () => void,
  startRecordingSearchApiLatency: () => void,
  stopRecordingSearchApiLatency: () => void,
  startRecordingSearchResponseProcessingTime: () => void,
  optionalQueryStrings?: Record<string, any>,
): Observable<FlightSearchResult> {
  const body = {
    ...(liveTestProviderCodes.length > 0 && liveTestProviderCodes),
    paymentMethodIds,
    search: {
      adultsCount: adultCount,
      appType,
      cabin: cabinClass,
      childrenCount: childrenCount,
      clientCreatedAt: new Date(),
      currencyCode,
      deviceType,
      infantsCount: infantCount,
      legs,
      locale,
      shopcashClickId,
      showWegoFares: true,
      siteCode: currentSiteCode,
      userLoggedIn: !!user,
      showWegoFaresOnly,
      showOfferEngineFares: isOfferEngineEnabled,
    },
    providersRestriction: {
      codes: providerRestrictions,
      type: 'BL',
    },
  };
  const queryStrings = {
    clientId: wegoAnalyticsClientId,
    currencyCode,
    locale,
    ...optionalQueryStrings,
  };

  const extra = {
    sessionId: wegoAnalyticsSessionId,
    partnerDebugKey: partnerDebugKey,
  };

  return flightSearchFromFetchObservable(
    url,
    queryStrings,
    body,
    user,
    isCacheFlightSearch,
    showTurnstileWidget,
    startRecordingSearchApiLatency,
    stopRecordingSearchApiLatency,
    startRecordingSearchResponseProcessingTime,
    extra,
  );
}

export function flightSearchFromFetchObservable(
  url: string,
  queryStrings: {
    locale: string;
    currencyCode: string;
    clientId: string | undefined;
    [key: string]: any;
  },
  body: any,
  user: User | null,
  isCacheFlightSearch: boolean,
  showTurnstileWidget: () => void,
  startRecordingSearchApiLatency: () => void,
  stopRecordingSearchApiLatency: () => void,
  startRecordingSearchResponseProcessingTime: () => void,
  extra?: any,
): Observable<FlightSearchResult> {
  let cacheKey = '';
  let cachedFlightSearchResponses: CachedFlightSearchResponse[] = [];

  if (isCacheFlightSearch) {
    //#region Caching Metasearch API response
    const bodyString = JSON.stringify(body);

    const jsonObject = JSON.parse(bodyString);
    // Deleting the clientCreatedAt key as it is unique each time
    delete jsonObject.search.clientCreatedAt;

    const bodyWithoutClientCreatedAt = JSON.stringify(jsonObject);

    cacheKey = JSON.stringify({ url, queryStrings, bodyWithoutClientCreatedAt, user, extra });

    cachedFlightSearchResponses =
      (localStorageGet(FLIGHT_SEARCH_RESPONSE_LOCAL_STORAGE_KEY) as CachedFlightSearchResponse[]) ||
      [];

    const cachedData = cachedFlightSearchResponses.find((cachedFlightSearchResponse) => {
      return (
        cachedFlightSearchResponse.cacheKey === cacheKey &&
        cachedFlightSearchResponse.expiryTime > Date.now()
      );
    });

    if (cachedData) {
      return of(cachedData.flightSearchResult);
    }
    //#endregion
  }
  startRecordingSearchApiLatency();

  return fromFetch(appendSearchParams(url, queryStrings), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      ...(!!getGenzoIdentifierId() && {
        'X-Wego-Genzo-Identifier-Id': getGenzoIdentifierId()!,
      }),
      'X-Wego-Client-Id': queryStrings.clientId || '',
      'X-Wego-Session-Id': extra?.sessionId || '',
      'X-Wego-Site-Code': body.search.siteCode,
      'Partner-Debug-Key': extra?.partnerDebugKey || '',
      ...(!!user?.email && { 'X-Wego-Hashed-User-Email': sha256(user.email).toString() }),
    },
    body: JSON.stringify(body),
  }).pipe(
    switchMap<Response, Promise<FlightSearchResult>>(async (response: Response) => {
      stopRecordingSearchApiLatency();
      startRecordingSearchResponseProcessingTime();
      if (response.ok) {
        const jsonResponse = (await response.json()) as FlightSearchResult;

        //#region Caching Metasearch API response
        if (isCacheFlightSearch) {
          const cachedFlightSearchResponsesWithValidExpiry = cachedFlightSearchResponses.filter(
            (cachedFlightSearchResponse) => {
              return cachedFlightSearchResponse.expiryTime > Date.now();
            },
          );

          cachedFlightSearchResponsesWithValidExpiry.push({
            cacheKey,
            flightSearchResult: jsonResponse,
            expiryTime: Date.now() + 1000 * 60 * 25,
          });

          if (cachedFlightSearchResponsesWithValidExpiry.length > 10) {
            cachedFlightSearchResponsesWithValidExpiry.shift();
          }

          localStorageSave(
            FLIGHT_SEARCH_RESPONSE_LOCAL_STORAGE_KEY,
            cachedFlightSearchResponsesWithValidExpiry,
          );
        }
        //#endregion

        return jsonResponse as FlightSearchResult;
      }

      if (
        response.headers.has('Cf-Mitigated') &&
        response.headers.get('Cf-Mitigated') === 'challenge'
      ) {
        showTurnstileWidget();
      }

      throw new Error(response.status.toString());
    }),
    share({
      resetOnError: false,
      resetOnComplete: false,
      resetOnRefCountZero: false,
    }),
  );
}
